import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { IRootState } from '../../../store/reducer';
import {
  setGames,
  setLockedTeaser,
  setIsLoading,
  setLastUpdatedTime,
  setLastUpdatedTimestamp,
  setError,
  setCurrentPage,
} from '../store/slice';
import { fetchPropsData } from '../data/propsApi';
import { transformPropsApiToGames, getAvailableSportsbooksFromResponse } from '../data/transformPropsData';
import { DisplayGame, PlayerData, PlayerProp, PropsApiResponse } from '../data/types';
import { HIDDEN_STATS, STAT_LABELS } from '../data/statLabels';
import { leagueHasMinutes, leagueHidesQuestionable } from '../data/leagueConfig';
import { getPropsOpenRule, getPropsOpenTime, isGameLocked } from '../data/propsAvailability';
import { useAuth } from '../../../hooks/useAuth';

const ITEMS_PER_PAGE = 50;
// Display order for the NFL position filter; anything else sorts alphabetically after these.
const POSITION_ORDER = ['QB', 'RB', 'WR', 'TE', 'K'];

/**
 * Placeholder values for a locked (teaser) row's withheld fields. NaN / '' rather than 0 so an
 * unguarded render is obviously wrong instead of plausibly right; every cell and card checks
 * `locked` before reading these. Declared once so the convention lives in one place.
 */
const LOCKED_VALUE_FIELDS = {
  projection: NaN,
  sportsbooks: [] as PlayerProp['sportsbooks'],
  book: '',
  bookIcon: '',
  bookLine: NaN,
  odds: '',
  oddsNum: NaN,
  delta: NaN,
  edge: NaN,
  bookWin: NaN,
  modelWin: NaN,
  marketWin: NaN,
  stokWin: NaN,
  evPct: NaN,
  side: 'Over' as const,
  deepLinkUrl: undefined as string | undefined,
  customMinutes: undefined as number | undefined,
  hasCustomizations: false,
};

interface UsePropsDataOptions {
  /**
   * true / false / null (unknown or signed out). Non-subscribers still fetch — the proxy returns
   * the teaser shape. Only the caller that passes this option FETCHES; callers that omit it
   * (e.g. FilterBottomSheet) just read the shared Redux data, so the board is requested once.
   */
  hasActiveSubscription?: boolean | null;
  /** Hard off-switch (e.g. the native app's no-access screen, which never renders the board). */
  skip?: boolean;
  /**
   * Teaser mode: persisted value filters (min/max EV, odds range, both-models) are not applied.
   * Locked rows have no values to filter on, and a filter left over from a past subscription
   * would silently hide the single free row. Identity filters (game, stat, position, search,
   * minutes, injury) still apply — they're the controls the viewer can see and change.
   */
  isTeaser?: boolean;
}

export function usePropsData(options: UsePropsDataOptions = {}) {
  const { hasActiveSubscription, skip = false, isTeaser = false } = options;
  const isFetcher = Object.prototype.hasOwnProperty.call(options, 'hasActiveSubscription');
  const dispatch = useDispatch();
  const { getToken, isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const propsState = useSelector((s: IRootState) => s.stokasticProps);
  const {
    games,
    lockedTeaser,
    filters,
    selectedGameIds,
    sortField,
    sortDirection,
    currentPage,
    isLoading,
    lastUpdatedTime,
    lastUpdatedTimestamp,
    playerCustomEVData,
    playerCustomizations,
  } = propsState;

  // Store last API response to extract available sportsbooks
  const lastResponseRef = useRef<PropsApiResponse | null>(null);

  // Use sorted string for stable queryKey - ensures array changes trigger refetch
  const sportsbooksKey = [...filters.selectedSportsbooks].sort().join(',');

  // Signed-in users wait for the subscription check (for THIS league — the hook reports null on a
  // league switch until the new check lands) so we don't fetch twice; signed-out users (null)
  // fetch straight away and get the teaser shape. The subscription state is part of the key so
  // unlocking after checkout re-fetches the full board without a reload.
  const subscriptionSettled = !isAuthenticated || hasActiveSubscription !== null;

  // On a league switch the route remounts with the new league in the URL while Redux still holds
  // the old one until PropsContent's effect syncs it. Never fetch the stale league.
  const { league: urlLeague } = useParams<{ league?: string }>();
  const leagueSynced = !urlLeague || urlLeague.toUpperCase() === filters.league;

  const { refetch: refetchProps, isInitialLoading: isLoadingProps } = useQuery({
    queryKey: ['props', filters.league, filters.state, sportsbooksKey, filters.stokWeight, filters.marketWeight, hasActiveSubscription === true],
    enabled: isFetcher && !skip && !isAuthLoading && subscriptionSettled && leagueSynced,
    // `signal` is React Query's cancellation for this query: when the key changes (league,
    // filters, or access flipping teaser → full) the superseded query is aborted. Consuming it
    // means an obsolete response can never land in Redux over the current board.
    queryFn: async ({ signal }) => {
      dispatch(setIsLoading(true));
      try {
        // Get access token for cross-origin API authentication
        const accessToken = await getToken();
        if (signal?.aborted) return [];

        const result = await fetchPropsData({
          leagues: [filters.league],
          state: filters.state || 'IL',
          sportsbooks: filters.selectedSportsbooks,
          accessToken,
          stokWeight: filters.stokWeight,
          marketWeight: filters.marketWeight,
          signal,
        });
        if (signal?.aborted) return [];

        if (result.success && result.data) {
          lastResponseRef.current = result.data;
          // Lives in Redux (like games): several components mount this hook and react-query runs the
          // fetch for only one of them, so local state here would land in the wrong instance.
          dispatch(setLockedTeaser(result.data.lockedTeaser ?? null));
          const transformedGames = transformPropsApiToGames(result.data, filters.league);
          dispatch(setGames(transformedGames));

          // Set update time from projectionsLastUpdated in API response
          const leagueKey = filters.league.toLowerCase();
          const projectionsTimestamp = result.data.projectionsLastUpdated?.[leagueKey];

          if (projectionsTimestamp) {
            const projDate = new Date(projectionsTimestamp);
            dispatch(
              setLastUpdatedTime(
                projDate.toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true,
                })
              )
            );
            dispatch(setLastUpdatedTimestamp(projDate.getTime()));
          } else {
            dispatch(setLastUpdatedTime('-'));
            dispatch(setLastUpdatedTimestamp(null));
          }

          return transformedGames;
        }

        dispatch(setError(result.error || 'Failed to load props data'));
        return [];
      } finally {
        // A superseded query must not clear the spinner the current one turned on
        if (!signal?.aborted) dispatch(setIsLoading(false));
      }
    },
    staleTime: 0, // Always consider data stale - refetch on every filter change
    cacheTime: 0, // Don't cache results (v4 syntax)
    refetchOnWindowFocus: false,
  });

  // Only the fetching instance may refetch: react-query v4's `refetch` ignores `enabled: false`,
  // so a call from a read-only instance (FilterBottomSheet) would send a second board request.
  const handleRefresh = useCallback(async () => {
    if (!isFetcher) return;
    dispatch(setIsLoading(true));
    try {
      await refetchProps();
    } finally {
      dispatch(setIsLoading(false));
    }
  }, [dispatch, refetchProps, isFetcher]);

  const availableSportsbooks = useMemo(() => {
    if (lastResponseRef.current) {
      return getAvailableSportsbooksFromResponse(lastResponseRef.current);
    }
    // Fallback: extract from games
    const books = new Set<string>();
    for (const game of games) {
      for (const player of game.players || []) {
        for (const prop of player.props || []) {
          for (const sb of prop.sportsbooks || []) {
            books.add(sb.book);
          }
        }
      }
    }
    return Array.from(books).sort();
  }, [games]);

  const currentGame = useMemo(() => {
    if (selectedGameIds.length === 0) return undefined;
    return games.find((g) => g.id === selectedGameIds[0]);
  }, [games, selectedGameIds]);

  // Props open window (NFL): re-evaluate once a minute so games unlock without a reload.
  // Leagues without a rule never tick, so their bets pipeline isn't recomputed on a timer.
  const hasOpenRule = getPropsOpenRule(filters.league) !== null;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!hasOpenRule) return undefined;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => window.clearInterval(id);
  }, [hasOpenRule]);

  const openTimes = useMemo(() => {
    const map = new Map<string, Date>();
    for (const game of games) {
      const openTime = getPropsOpenTime(game.startTime, filters.league);
      if (openTime) map.set(game.id, openTime);
    }
    return map;
  }, [games, filters.league]);

  const lockedGameIds = useMemo(() => {
    const set = new Set<string>();
    if (!hasOpenRule) return set;
    for (const game of games) {
      if (isGameLocked(game.startTime, filters.league, now)) set.add(game.id);
    }
    return set;
  }, [games, filters.league, now, hasOpenRule]);

  // When a game crosses its open time while the page is open, refetch before showing it: the slate in
  // memory may be hours old, and the explainer promised props open once projections are current.
  const prevLockedRef = useRef<{ league: string; ids: Set<string> } | null>(null);
  useEffect(() => {
    const prev = prevLockedRef.current;
    prevLockedRef.current = { league: filters.league, ids: lockedGameIds };
    if (!prev || prev.league !== filters.league) return; // first run, or a league switch (games were cleared)
    let unlocked = false;
    prev.ids.forEach((id) => {
      // only a game that is still on the slate and is no longer locked counts as an unlock
      if (!lockedGameIds.has(id) && games.some((g) => g.id === id)) unlocked = true;
    });
    if (unlocked && isFetcher) void refetchProps();
  }, [lockedGameIds, games, filters.league, refetchProps, isFetcher]);

  /** Games whose props are visible; everything bet-related below is built from these only. */
  const openGames = useMemo(
    () => (lockedGameIds.size === 0 ? games : games.filter((g) => !lockedGameIds.has(g.id))),
    [games, lockedGameIds]
  );

  /** The soonest game still to open, for the "next up" line when nothing is open yet. */
  const nextOpen = useMemo(() => {
    let best: { game: DisplayGame; openTime: Date } | undefined;
    for (const game of games) {
      if (!lockedGameIds.has(game.id)) continue;
      const openTime = openTimes.get(game.id);
      if (openTime && (!best || openTime < best.openTime)) best = { game, openTime };
    }
    return best;
  }, [games, lockedGameIds, openTimes]);

  const getGameOpenTime = useCallback((gameId: string) => openTimes.get(gameId), [openTimes]);
  const isGameIdLocked = useCallback((gameId: string) => lockedGameIds.has(gameId), [lockedGameIds]);

  // Get player props directly from player data
  const getDynamicPlayerProps = useCallback((player: PlayerData) => {
    return player.props || [];
  }, []);

  // Build all bets from games - flattens props with player/game context
  const allBets = useMemo(() => {
    const bets = openGames.flatMap((game) =>
      (game.players || []).flatMap((player) => {
        // Find injured teammates (same team, different player, has injury status, projected minutes > 0)
        const injuredTeammates = (game.players || [])
          .filter((p) => {
            const playerMinutes = typeof p.minutes === 'string' ? parseFloat(p.minutes) : Number(p.minutes);
            return (
              p.team === player.team &&
              p.id !== player.id &&
              p.injuryStatus &&
              ['out', 'doubtful', 'questionable'].includes(p.injuryStatus.toLowerCase()) &&
              !isNaN(playerMinutes) &&
              playerMinutes > 0
            );
          })
          .map((p) => ({
            name: p.name,
            status: p.injuryStatus,
            notes: p.injuryNotes,
          }));

        // Check if this player has custom EV data and customizations
        const customEVData = playerCustomEVData[player.id];
        const customization = playerCustomizations[player.id];
        const hasCustomizations = !!customization;
        const customMinutes = customization?.minutes;

        return (player.props || [])
          // Locked teaser rows have no books by design (stripped server-side) and still render
          .filter((prop) => prop.locked || prop.sportsbooks.length > 0)
          .map((prop) => {
            const playerFields = {
              playerName: player.name,
              playerId: player.id,
              team: player.team,
              avatar: player.avatar,
              position: player.position,
              minutes: player.minutes,
              projections: player.projections,
              injuryStatus: player.injuryStatus,
              injuryNotes: player.injuryNotes,
              battingOrder: player.battingOrder,
              confirmedLineup: player.confirmedLineup,
              hand: player.hand,
              injuredTeammates, // Teammates with injuries
              // Game info
              matchup: game.matchup,
              startDate: game.time,
              gameId: game.id,
            };

            if (prop.locked) {
              // Teaser row: identity only — see LOCKED_VALUE_FIELDS.
              return {
                stat: prop.stat,
                originalStat: prop.originalStat,
                ...LOCKED_VALUE_FIELDS,
                locked: true,
                lockedRank: prop.lockedRank,
                ...playerFields,
              };
            }

            const best = prop.sportsbooks[0]; // Best book (sorted by EV)

            // Look up custom EV for this prop if available
            let customEV: number | null = null;
            let customModelWin: number | null = null;

            if (customEVData?.outcomes) {
              const normalizedStat = STAT_LABELS[prop.stat] || prop.stat;
              const outcome = customEVData.outcomes.find(
                (o) =>
                  (o.statType === prop.stat ||
                    o.statType === normalizedStat ||
                    STAT_LABELS[o.statType] === normalizedStat) &&
                  o.label === best.side
              );

              if (outcome) {
                const sbResult = outcome.sportsbooks.find(
                  (sb) => sb.code.toLowerCase() === best.book.toLowerCase()
                );
                if (sbResult) {
                  // blendedEV is a decimal (e.g., 0.94 for 94%), needs * 100
                  customEV = sbResult.blendedEV !== null ? sbResult.blendedEV * 100 : null;
                  // blendedTrueWinProbability is already a percentage (e.g., 98 for 98%)
                  customModelWin = sbResult.blendedTrueWinProbability;
                }
              }
            }

            // Use custom values if available, otherwise fall back to original
            const finalEvPct = customEV !== null ? customEV : best.evPct;
            const finalModelWin = customModelWin !== null ? customModelWin : best.modelWin;

            return {
              // Prop info
              stat: prop.stat,
              originalStat: prop.originalStat, // Original stat name from API for filtering
              projection: prop.projection,
              sportsbooks: prop.sportsbooks,
              // Best book info (for quick access in table/filters)
              book: best.book,
              bookIcon: best.bookIcon,
              bookLine: best.line,
              odds: best.americanOdds,
              oddsNum: best.odds,
              delta: best.delta,
              edge: best.edge,
              bookWin: best.bookWin,
              modelWin: finalModelWin,
              marketWin: best.marketWin,
              stokWin: best.stokWin,
              evPct: finalEvPct,
              side: best.side,
              deepLinkUrl: best.deepLinkUrl,
              // Custom projection info
              customMinutes,
              hasCustomizations,
              locked: false,
              lockedRank: undefined as number | undefined,
              // Player + game info
              ...playerFields,
            } as typeof LOCKED_VALUE_FIELDS & typeof playerFields & { stat: string; originalStat: string; locked: boolean; lockedRank?: number };
          });
      })
    );
    return bets;
  }, [openGames, playerCustomEVData, playerCustomizations]);

  // Extract unique original stats from all bets for dynamic stat filter options
  const availableStats = useMemo(() => {
    const statsSet = new Set<string>();
    allBets.forEach((bet) => {
      if (bet.originalStat && !HIDDEN_STATS.has(bet.stat)) {
        statsSet.add(bet.originalStat);
      }
    });
    return Array.from(statsSet).sort();
  }, [allBets]);

  // Distinct positions in the feed (NFL position filter), in football order
  const availablePositions = useMemo(() => {
    const set = new Set<string>();
    allBets.forEach((bet) => {
      if (bet.position) set.add(bet.position.toUpperCase());
    });
    return Array.from(set).sort((a, b) => {
      const ia = POSITION_ORDER.indexOf(a);
      const ib = POSITION_ORDER.indexOf(b);
      if (ia === -1 && ib === -1) return a.localeCompare(b);
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
  }, [allBets]);

  const filteredBets = useMemo(() => {
    const searchTerm = filters.playerSearch?.toLowerCase().trim() || '';
    // A position the current feed doesn't offer (stale/persisted) behaves as All rather than hiding every bet
    const positionFilter =
      filters.positionFilter && filters.positionFilter !== 'All' && availablePositions.includes(filters.positionFilter.toUpperCase())
        ? filters.positionFilter.toUpperCase()
        : null;

    /** Filters that read the fields the proxy withholds from locked rows. */
    const passesValueFilters = (bet: (typeof allBets)[number]): boolean => {
      if (filters.minLineFilter && !(bet.bookLine >= parseFloat(filters.minLineFilter))) return false;
      if (!(bet.evPct >= filters.minEdge)) return false;
      if (filters.maxEV > 0 && !(bet.evPct <= filters.maxEV)) return false;
      if (filters.minOdds !== 0 || filters.maxOdds !== 0) {
        const oddsNum = parseInt(bet.odds.replace('+', ''), 10);
        if (isNaN(oddsNum)) return false;
        if (filters.minOdds !== 0 && oddsNum < filters.minOdds) return false;
        if (filters.maxOdds !== 0 && oddsNum > filters.maxOdds) return false;
      }
      // Outcomes without market projections aren't shown
      if (!(bet.marketWin && bet.marketWin > 0)) return false;
      if (filters.requireBothModelsEV && !(bet.stokWin > bet.bookWin && bet.marketWin > bet.bookWin)) return false;
      return true;
    };

    /**
     * Filters driven by controls the viewer can see and change (stat, position, search, minutes,
     * injured teammate). Several are persisted across sessions — in teaser mode the single free
     * row is exempt from them so a value left over from a past visit can't silently hide it.
     * The game selection is not exempt: narrowing to one game is an explicit, visible choice.
     */
    const passesIdentityFilters = (bet: (typeof allBets)[number]): boolean => {
      if (filters.selectedStatFilter !== 'All' && bet.originalStat !== filters.selectedStatFilter) return false;
      if (positionFilter && bet.position?.toUpperCase() !== positionFilter) return false;
      if (
        searchTerm &&
        !bet.playerName.toLowerCase().includes(searchTerm) &&
        !bet.team.toLowerCase().includes(searchTerm) &&
        !bet.originalStat.toLowerCase().includes(searchTerm)
      ) return false;
      // minMinutes only applies to leagues with projected minutes (NBA/NHL)
      if (leagueHasMinutes(filters.league)) {
        const playerMinutes =
          bet.customMinutes != null
            ? bet.customMinutes
            : typeof bet.minutes === 'string'
              ? parseFloat(bet.minutes)
              : Number(bet.minutes);
        if (!isNaN(playerMinutes) && playerMinutes < filters.minMinutes) return false;
      }
      if (filters.hideInjuredTeammate && bet.injuredTeammates && bet.injuredTeammates.length > 0) return false;
      return true;
    };

    const isFreeTeaserRow = (bet: (typeof allBets)[number]) => isTeaser && !bet.locked;

    return allBets
      .filter((bet) => selectedGameIds.length === 0 || selectedGameIds.includes(bet.gameId))
      // Filter out hidden stat types
      .filter((bet) => !HIDDEN_STATS.has(bet.stat))
      // Filter out questionable injury status (unreliable data) — except in the NFL, where it's routine
      .filter((bet) => !leagueHidesQuestionable(filters.league) || bet.injuryStatus?.toLowerCase() !== 'questionable')
      .filter((bet) => isFreeTeaserRow(bet) || passesIdentityFilters(bet))
      // Value filters apply to priced rows only. Locked teaser rows carry no values (the proxy
      // withheld them) and the free teaser row must not be hidden by a persisted value — both
      // pass here by construction, so a new value filter can't accidentally drop them.
      .filter((bet) => bet.locked || isTeaser || passesValueFilters(bet));
  }, [allBets, filters, selectedGameIds, availablePositions, isTeaser]);

  const sortedBets = useMemo(() => {
    return [...filteredBets].sort((a, b) => {
      // Teaser: the free row(s) lead, locked rows follow in the server's EV order. Locked rows
      // have no sortable values, so the chosen sort field can't apply to them.
      if (a.locked || b.locked) {
        if (a.locked && b.locked) return (a.lockedRank ?? Number.MAX_SAFE_INTEGER) - (b.lockedRank ?? Number.MAX_SAFE_INTEGER);
        return a.locked ? 1 : -1;
      }

      let valA: number;
      let valB: number;

      switch (sortField) {
        case 'delta':
          valA = a.delta;
          valB = b.delta;
          break;
        case 'projection':
          valA = a.projection;
          valB = b.projection;
          break;
        case 'bookLine':
          valA = a.bookLine;
          valB = b.bookLine;
          break;
        case 'bookWin':
          valA = a.bookWin;
          valB = b.bookWin;
          break;
        case 'modelWin':
          valA = a.modelWin;
          valB = b.modelWin;
          break;
        case 'evPct':
        default:
          valA = a.evPct;
          valB = b.evPct;
          break;
      }

      return sortDirection === 'desc' ? valB - valA : valA - valB;
    });
  }, [filteredBets, sortField, sortDirection]);

  /** Unlocked rows on the board (teaser: normally 1; subscribers: all). */
  const freeCount = useMemo(() => sortedBets.reduce((n, bet) => (bet.locked ? n : n + 1), 0), [sortedBets]);

  const totalPages = Math.ceil(sortedBets.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedBets = sortedBets.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      dispatch(setCurrentPage(totalPages));
    }
  }, [totalPages, currentPage, dispatch]);

  return {
    games,
    openGames,
    lockedGameIds,
    nextOpen,
    getGameOpenTime,
    isGameIdLocked,
    currentGame,
    allBets,
    filteredBets,
    sortedBets,
    freeCount,
    paginatedBets,
    totalPages,
    startIndex,
    itemsPerPage: ITEMS_PER_PAGE,
    availableSportsbooks,
    availableStats,
    availablePositions,
    isLoading: isLoading || isLoadingProps,
    lastUpdatedTime,
    lastUpdatedTimestamp,
    lockedTeaser,
    handleRefresh,
    getDynamicPlayerProps,
  };
}
